/**
 * Utility: Methods for `window.AppComponents.utility`
 *
 * Note: Added to window.AppComponents via setupGlobalComponentsObj()
 *
 * @module internal/IComponentUtils
 */


// ƒ
// ============================================================================

/**
 * Get a component's data. Returns null if no data is available
 *
 * Basically gives you full control over any components, just by targetting
 * their container
 *
 * @example getComponentData( $myContainer, 'accordion' );
 * @example window.AppComponents.utility.getComponentData( $myContainer, 'accordion' );
 *
 * @param   {jQuery}  $container jQuery container element (the element that uses the main data-c-{componentName} attribute)
 * @param   {string}  name      Component name (eg "accordion")
 *
 * @return  {object|null}        Data object if available, null otherwise
 */
function getComponentData( $container = null, name = null )
{
	let isValid = true;

	// Validation
	if ( !$container )
	{
		console.warn( '[getComponentData] Missing container (as 1st arg).' );
		isValid = false;
	}

	if ( typeof( $container ) !== 'object' )
	{
		console.warn( '[getComponentData] Supplied container is not an object. Expected a jQuery element object (as 1st arg).' );
		isValid = false;
	}

	if ( !( $container instanceof jQuery ) )
	{
		console.warn( '[getComponentData] Supplied container is not a jQuery object (1st arg).' );
		isValid = false;
	}

	if ( !name )
	{
		console.warn( '[getComponentData] Missing component name (as 2nd arg)' );
		isValid = false;
	}

	if ( typeof( name ) !== 'string' )
	{
		console.warn( '[getComponentData] Supplied component name was not a string (as 2nd arg)' );
		isValid = false;
	}

	// Log an error & bail if any validation issues occured
	if ( !isValid )
	{
		console.error( '[getComponentData] Error(s) occured due to incorrect args' );
		return;
	}

	// Component data is added to the container object in the setup.
	// But this approach isn't documented because it's better to use these
	// accessors, which are standardised and easy to Ctrl + F for
	const elCData = $container.data( '_components' );

	// Optional chaining (?.) (shorter form of repeated "hasOwnProperty")
	return elCData?.components?.[name] || null;
}

/**
 * Get a component's Instance. Returns null if no data is available
 *
 * @see     getComponentData
 * @example getComponentInstance( $myContainer, 'accordion' );
 * @example window.AppComponents.utility.getComponentInstance( $myContainer, 'accordion' );
 */
function getComponentInstance( $container, name )
{
	return getComponentData( $container, name )?.instance || null;
}

/**
 * Get a component's Index. Returns null if no data is available
 *
 * @see     getComponentData
 * @example getComponentIndex( $myContainer, 'accordion' );
 * @example window.AppComponents.utility.getComponentIndex( $myContainer, 'accordion' );
 */
function getComponentIndex( $container, name )
{
	return getComponentData( $container, name )?.index || null;
}

/**
 * Get a component's Number. Returns null if no data is available
 *
 * @see     getComponentData
 * @example getComponentNumber( $myContainer, 'accordion' );
 * @example window.AppComponents.utility.getComponentNumber( $myContainer, 'accordion' );
 */
function getComponentNumber( $container, name )
{
	return getComponentData( $container, name )?.number || null;
}

/**
 * Check the supplied container to see it has an initialised instance of the specified component
 *
 * @example hasComponent( $myContainer, 'accordion' );
 * @example window.AppComponents.utility.hasComponent( $myContainer, 'accordion' );
 *
 * @param   {jQuery}  $container jQuery container element (the element that uses the main data-c-{componentName} attribute)
 * @param   {string}  name      Component name (eg "accordion")
 *
 * @return  {bool}               True if it uses the component, false otherwise
 */
function hasComponent( $container, name )
{
	return ( getComponentData( $container, name ) ) ? true : false;
}


// Exports
// ============================================================================

export const IComponentUtils = { getComponentData, getComponentIndex, getComponentNumber, getComponentInstance, hasComponent };
